import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { CompanyPicker, type CompanyPickerValue } from "@/components/company-picker";
import { PriceTablePickerModal } from "@/components/price-table/PriceTablePickerModal";
import { useCreateDatasheet, useDatasheetTypes, type SizeSlot } from "@/lib/datasheets";
import { describeMode } from "./mode-description";

interface Row { desc: string; quantityAvail: number; unitPriceExGst: number | null; }
const emptyRow = (): Row => ({ desc: "", quantityAvail: 0, unitPriceExGst: null });

export function DatasheetForm() {
  const navigate = useNavigate();
  const create = useCreateDatasheet();
  const { data: types } = useDatasheetTypes();

  const [company, setCompany] = useState<CompanyPickerValue | null>(null);
  const [contact, setContact] = useState<ClientPickerValue | null>(null);
  const [typeCode, setTypeCode] = useState("");
  const [department, setDepartment] = useState("");
  const [priceTableId, setPriceTableId] = useState<number | null>(null);
  const [priceTableOpen, setPriceTableOpen] = useState(false);
  const [isPrepaid, setIsPrepaid] = useState(false);
  const [clientStock, setClientStock] = useState(false);
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [note, setNote] = useState("");

  const canSubmit = company !== null && typeCode !== "" && !create.isPending;

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  async function submit() {
    if (!company) return;
    const sizes: SizeSlot[] = rows.map((r, i) => ({
      index: i + 1, desc: r.desc || null, productId: null,
      quantityAvail: r.quantityAvail, unitPriceExGst: r.unitPriceExGst,
    }));
    try {
      const created = await create.mutateAsync({
        companyId: company.id, datasheetTypeCode: typeCode, department: department || null,
        primaryContactId: contact?.clientId ?? null, priceTableId,
        isPrepaid, clientSuppliedStock: clientStock, sizes, note: note || null,
      });
      toast.success("Datasheet created");
      void navigate(`/datasheets/${created.datasheetId}`);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 400 ? "Invalid — check the fields." : "Could not create the datasheet");
    }
  }

  return (
    <div className="max-w-2xl space-y-4 p-6">
      <h1 className="text-xl font-semibold">New datasheet</h1>
      <div className="space-y-1">
        <Label>Company</Label>
        <CompanyPicker value={company} onChange={setCompany} />
      </div>
      <div className="space-y-1">
        <Label>Primary contact</Label>
        <ClientPicker value={contact} onChange={setContact} placeholder="Select contact…" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="type">Datasheet type</Label>
        <select id="type" aria-label="Datasheet type" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
          <option value="">Select type…</option>
          {(types ?? []).map((t) => <option key={t.datasheetTypeCode} value={t.datasheetTypeCode}>{t.description}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="dept">Department</Label>
        <Input id="dept" value={department} onChange={(e) => setDepartment(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Price table</Label>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => setPriceTableOpen(true)}>
            {priceTableId ? `Table #${priceTableId}` : "Select price table…"}
          </Button>
          {priceTableId ? <Button type="button" variant="ghost" onClick={() => setPriceTableId(null)}>Clear</Button> : null}
        </div>
        <PriceTablePickerModal open={priceTableOpen} onOpenChange={setPriceTableOpen}
          onConfirm={(v) => { setPriceTableId(v.priceTableId); setPriceTableOpen(false); }} />
      </div>
      <fieldset className="space-y-2 rounded-md border p-3">
        <legend className="px-1 text-sm font-medium">Mode</legend>
        <label htmlFor="df-prepaid" className="flex items-center gap-2 text-sm">
          <Checkbox id="df-prepaid" checked={isPrepaid} onCheckedChange={(v) => setIsPrepaid(v === true)} aria-label="Prepaid" /> Prepaid
        </label>
        <label htmlFor="df-clientstock" className="flex items-center gap-2 text-sm">
          <Checkbox id="df-clientstock" checked={clientStock} onCheckedChange={(v) => setClientStock(v === true)} aria-label="Client-supplied stock" /> Client-supplied stock
        </label>
        <p className="text-xs text-muted-foreground">{describeMode(isPrepaid, clientStock)}</p>
      </fieldset>
      <div className="space-y-2">
        <Label>Sizes</Label>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input aria-label={`Size ${i + 1} description`} placeholder="Description"
              value={r.desc} onChange={(e) => setRow(i, { desc: e.target.value })} />
            <Input aria-label={`Size ${i + 1} quantity`} type="number" className="w-24" value={r.quantityAvail || ""}
              onChange={(e) => setRow(i, { quantityAvail: Number(e.target.value) || 0 })} />
            <Input aria-label={`Size ${i + 1} price`} type="number" className="w-28" value={r.unitPriceExGst ?? ""}
              onChange={(e) => setRow(i, { unitPriceExGst: e.target.value === "" ? null : Number(e.target.value) })} />
            {rows.length > 1 ? (
              <Button type="button" variant="ghost" size="sm" aria-label="Remove size"
                onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>×</Button>
            ) : null}
          </div>
        ))}
        {rows.length < 7 ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setRows((prev) => [...prev, emptyRow()])}>Add size</Button>
        ) : null}
      </div>
      <div className="space-y-1">
        <Label htmlFor="note">Note</Label>
        <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => void submit()} disabled={!canSubmit}>Create datasheet</Button>
        <Button variant="outline" onClick={() => void navigate("/datasheets")}>Cancel</Button>
      </div>
    </div>
  );
}
